/**
 * DashboardPage — список детей с балансами и расписанием.
 * Маршрут: /cabinet
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack, Group, Box, Text, Title, Paper, Button, Loader, Center, Badge,
  SimpleGrid, Divider,
} from '@mantine/core';
import { IconHistory, IconPlus, IconCalendarEvent, IconTrophy, IconAlertTriangle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CabinetLayout from '../components/CabinetLayout';
import StudentCompetitionBanner from '../components/StudentCompetitionBanner';
import { cabinetService } from '../services/api';
import tokens from '../theme/designTokens';

const formatRub = (val) => {
  const num = Number(val);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  });
};

const formatLessonDate = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Moscow',
  });
};

// Цвет предмета (стабильный мап)
const SUBJECT_COLORS = {
  WeDo: { bg: '#FFF3E0', color: tokens.colors.brand.orange },
  Minecraft: { bg: '#DCFCE7', color: tokens.colors.brand.green },
  MS: { bg: '#E0F2FE', color: tokens.colors.brand.blue },
  Робототехника: { bg: '#EDE9FE', color: tokens.colors.brand.purple },
};

const getSubjectPalette = (name) => {
  return SUBJECT_COLORS[name] || { bg: tokens.colors.bg.softPurple, color: tokens.colors.brand.purple };
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [balances, setBalances] = useState({}); // { studentId: { balance, remaining_lessons, per_lesson_price } }
  const [upcomingLessons, setUpcomingLessons] = useState({}); // { studentId: [lessons] }
  const [competitions, setCompetitions] = useState([]); // ActiveCompetitionResponse[]
  const [paymentsAvailable, setPaymentsAvailable] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsData, compsData, paymentMode] = await Promise.all([
        cabinetService.getMyStudents(),
        cabinetService.getActiveCompetitions().catch(() => []),
        cabinetService.getPaymentMode().catch(() => ({ payments_available: true, maintenance_mode: false })),
      ]);
      setPaymentsAvailable(paymentMode.payments_available);
      setMaintenanceMode(paymentMode.maintenance_mode);
      const items = studentsData.items || studentsData;
      setStudents(items);
      setCompetitions(compsData || []);

      const bals = {};
      const lessons = {};
      await Promise.all(
        items.map(async (s) => {
          try {
            const [b, l] = await Promise.all([
              cabinetService.getBalance(s.id),
              cabinetService.getUpcomingLessons(s.id, 4),
            ]);
            bals[s.id] = b; // сохраняем полный объект { balance, remaining_lessons, per_lesson_price }
            lessons[s.id] = l;
          } catch {
            bals[s.id] = { balance: null, remaining_lessons: 0, per_lesson_price: null };
            lessons[s.id] = [];
          }
        })
      );
      setBalances(bals);
      setUpcomingLessons(lessons);
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <CabinetLayout>
        <Center py={80}>
          <Loader color="brand-purple" />
        </Center>
      </CabinetLayout>
    );
  }

  return (
    <CabinetLayout>
      <Stack gap="lg">
        <Box>
          <Title
            order={2}
            style={{
              fontFamily: tokens.typography.fontFamilyHeading,
              color: tokens.colors.text.primary,
            }}
          >
            Мои дети
          </Title>
          <Text c={tokens.colors.text.muted} fw={700} mt={4}>
            Управляйте балансом и оплатой занятий
          </Text>
        </Box>

        {/* Баннер тех. работ (test mode для не-тестовых пользователей) */}
        {maintenanceMode && (
          <Paper
            p="md"
            radius="xl"
            style={{
              background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
              border: '2px solid rgba(255,152,0,0.3)',
            }}
          >
            <Group gap="sm" align="center">
              <IconAlertTriangle size={22} color="#E65100" stroke={2.5} />
              <Box>
                <Text fw={900} style={{ color: '#E65100', fontFamily: tokens.typography.fontFamilyHeading }}>
                  Технические работы
                </Text>
                <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                  В данный момент проводятся технические работы. Оплата временно недоступна.
                </Text>
              </Box>
            </Group>
          </Paper>
        )}

        {/* Баннеры соревнований для родителей БЕЗ детей */}
        {students.length === 0 && competitions.length > 0 && (
          <Stack gap="sm">
            {competitions.map((comp) => (
              <Paper
                key={comp.id}
                p="md"
                radius="xl"
                style={{
                  background: tokens.gradients.softPurple,
                  border: '2px solid rgba(124,58,237,0.1)',
                }}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
                    <IconTrophy size={20} color={tokens.colors.brand.purple} stroke={2.5} />
                    <Box>
                      <Text size="sm" fw={800} style={{ color: tokens.colors.text.primary }}>
                        {comp.name}
                      </Text>
                      <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                        {new Date(comp.event_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'Europe/Moscow' })}
                      </Text>
                    </Box>
                    {comp.price_with_discount && (
                      <Group gap={4} align="baseline">
                        <Text fw={900} size="sm" style={{ color: tokens.colors.brand.orange }}>
                          {Number(comp.price_with_discount).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                        </Text>
                        {comp.discount_active && Number(comp.price) !== Number(comp.price_with_discount) && (
                          <Text size="xs" fw={700} style={{ color: tokens.colors.text.muted, textDecoration: 'line-through' }}>
                            {Number(comp.price).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                          </Text>
                        )}
                      </Group>
                    )}
                  </Group>
                  <Button
                    size="xs"
                    color="brand-purple"
                    radius={100}
                    onClick={() => navigate(`/competitions/${comp.id}`)}
                  >
                    Записаться
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {students.length === 0 ? (
          <Paper p="xl" radius="xl" style={{ background: 'white', textAlign: 'center' }}>
            <Text c={tokens.colors.text.muted} fw={700}>
              К вашему профилю пока не привязаны дети.
              Обратитесь к менеджеру вашего центра.
            </Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {students.map((student) => {
              const balData = balances[student.id] || {};
              const balanceNum = balData.balance != null ? Number(balData.balance) : null;
              const hasDebt = balanceNum != null && balanceNum < 0;
              const remainingLessons = balData.remaining_lessons || 0;
              const lessons = upcomingLessons[student.id] || [];

              // Кнопка пополнения доступна только для B2C/individual с группами
              const orgs = student.organizations || [];
              const canTopup = orgs.length > 0 && orgs.some(
                (o) => !o.organization_type || o.organization_type === 'b2c' || o.organization_type === 'individual'
              );

              return (
                <Stack key={student.id} gap="sm">
                  {/* Баннеры соревнований для этого ребёнка */}
                  {competitions.map((comp) => (
                    <StudentCompetitionBanner
                      key={comp.id}
                      student={student}
                      competition={comp}
                      onUpdate={loadData}
                      paymentsAvailable={paymentsAvailable}
                    />
                  ))}

                  {/* Карточка ребёнка с балансом */}
                  <Paper
                    p="xl"
                    radius="xl"
                    style={{
                      background: 'white',
                      border: `2px solid ${hasDebt ? 'rgba(244,63,142,0.3)' : 'rgba(124,58,237,0.08)'}`,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Stack gap="md">
                      <Group gap="md" align="center">
                        <Box
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 18,
                            background: tokens.gradients.softPurple,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 24,
                            fontFamily: tokens.typography.fontFamilyHeading,
                            fontWeight: 1000,
                            color: tokens.colors.brand.purple,
                            flexShrink: 0,
                          }}
                        >
                          {(student.first_name || '?')[0]}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            size="lg"
                            fw={900}
                            style={{
                              color: tokens.colors.text.primary,
                              fontFamily: tokens.typography.fontFamilyHeading,
                              lineHeight: 1.15,
                            }}
                          >
                            {[student.first_name, student.last_name].filter(Boolean).join(' ') || 'Без имени'}
                          </Text>
                          {student.organizations && student.organizations.length > 0 && (
                            <Text size="xs" c={tokens.colors.text.muted} fw={700} mt={2}>
                              {student.organizations.map((o) => o.name).join(', ')}
                            </Text>
                          )}
                        </Box>
                      </Group>

                      {/* Баланс: занятия слева + сумма справа */}
                      <Box
                        p="md"
                        style={{
                          background: hasDebt
                            ? 'linear-gradient(135deg,#FFE4EC,#FCE7F3)'
                            : tokens.gradients.softPurple,
                          borderRadius: 16,
                        }}
                      >
                        <Group justify="space-between" align="center">
                          <Box>
                            <Text size="xs" c={tokens.colors.text.muted} fw={800} tt="uppercase">
                              {hasDebt ? 'Долг' : 'Доступно занятий'}
                            </Text>
                            <Text
                              style={{
                                fontFamily: tokens.typography.fontFamilyHeading,
                                fontWeight: 1000,
                                fontSize: 28,
                                color: hasDebt ? tokens.colors.brand.pink : tokens.colors.brand.purple,
                                lineHeight: 1.1,
                              }}
                            >
                              {hasDebt ? formatRub(Math.abs(balanceNum)) : `${remainingLessons}`}
                            </Text>
                          </Box>
                          <Box style={{ textAlign: 'right' }}>
                            <Text size="xs" c={tokens.colors.text.muted} fw={800} tt="uppercase">
                              Сумма
                            </Text>
                            <Text
                              style={{
                                fontFamily: tokens.typography.fontFamilyHeading,
                                fontWeight: 1000,
                                fontSize: 22,
                                color: hasDebt ? tokens.colors.brand.pink : tokens.colors.brand.purple,
                                lineHeight: 1.1,
                              }}
                            >
                              {balanceNum != null ? formatRub(balanceNum) : '—'}
                            </Text>
                          </Box>
                        </Group>
                      </Box>

                      <Group gap="xs" grow>
                        <Button
                          leftSection={<IconPlus size={16} />}
                          color={canTopup && paymentsAvailable ? "brand-orange" : "gray"}
                          disabled={!canTopup || !paymentsAvailable}
                          onClick={() => canTopup && paymentsAvailable && navigate(`/students/${student.id}/topup`)}
                        >
                          Пополнить
                        </Button>
                        <Button
                          leftSection={<IconHistory size={16} />}
                          variant="light"
                          color="brand-purple"
                          onClick={() => navigate(`/students/${student.id}/history`)}
                        >
                          История
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>

                  {/* Расписание ближайших занятий */}
                  <Paper p="lg" radius="xl" style={{ background: 'white' }}>
                    <Group gap="xs" align="center" mb="sm">
                      <Box
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          background: tokens.colors.bg.softYellow,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: tokens.colors.brand.orange,
                        }}
                      >
                        <IconCalendarEvent size={18} stroke={2.5} />
                      </Box>
                      <Text
                        fw={900}
                        style={{
                          fontFamily: tokens.typography.fontFamilyHeading,
                          color: tokens.colors.text.primary,
                        }}
                      >
                        Ближайшие занятия
                      </Text>
                    </Group>

                    {lessons.length === 0 ? (
                      <Text size="sm" c={tokens.colors.text.muted} fw={700} ta="center" py="md">
                        Нет запланированных занятий
                      </Text>
                    ) : (
                      <Stack gap={0}>
                        {lessons.map((lesson, idx) => {
                          const palette = getSubjectPalette(lesson.subject_name);
                          return (
                            <React.Fragment key={lesson.id}>
                              {idx > 0 && <Divider color="rgba(124,58,237,0.06)" />}
                              <Group gap="sm" py="sm" wrap="nowrap">
                                {/* Дата */}
                                <Box
                                  style={{
                                    minWidth: 48,
                                    flexShrink: 0,
                                    textAlign: 'center',
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontFamily: tokens.typography.fontFamilyHeading,
                                      fontWeight: 1000,
                                      fontSize: 20,
                                      color: tokens.colors.brand.purple,
                                      lineHeight: 1,
                                    }}
                                  >
                                    {new Date(lesson.scheduled_date + 'T00:00:00').getDate()}
                                  </Text>
                                  <Text
                                    size="xs"
                                    fw={800}
                                    tt="uppercase"
                                    c={tokens.colors.text.muted}
                                    mt={2}
                                  >
                                    {new Date(lesson.scheduled_date + 'T00:00:00').toLocaleDateString('ru-RU', { month: 'short' }).replace('.', '')}
                                  </Text>
                                </Box>

                                {/* Предмет + время */}
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Badge
                                    size="sm"
                                    variant="filled"
                                    radius="xl"
                                    styles={{
                                      root: {
                                        background: palette.bg,
                                        color: palette.color,
                                      },
                                    }}
                                  >
                                    {lesson.subject_name || lesson.group_name}
                                  </Badge>
                                  <Text
                                    size="xs"
                                    c={tokens.colors.text.muted}
                                    fw={700}
                                    mt={4}
                                  >
                                    {lesson.day_name}, {formatLessonDate(lesson.scheduled_date)}
                                  </Text>
                                </Box>

                                {/* Время */}
                                <Box style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <Text
                                    fw={900}
                                    style={{
                                      fontFamily: tokens.typography.fontFamilyHeading,
                                      color: tokens.colors.text.primary,
                                      fontSize: 15,
                                    }}
                                  >
                                    {lesson.start_time}
                                  </Text>
                                  <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                                    до {lesson.end_time}
                                  </Text>
                                </Box>
                              </Group>
                            </React.Fragment>
                          );
                        })}
                      </Stack>
                    )}
                  </Paper>
                </Stack>
              );
            })}
          </SimpleGrid>
        )}
      </Stack>
    </CabinetLayout>
  );
};

export default DashboardPage;
